import { ZipReader, Uint8ArrayReader, TextWriter, Uint8ArrayWriter, BlobReader, } from '@zip.js/zip.js';
import { FileCollection } from "./FileCollection.js";
import { sourceItemToExtendedSourceItem } from "./append/sourceItemToExtendedSourceItem.js";
/**
 * Creates a FileCollection from an IUM zip file.
 * @param zipContent - The content of the IUM zip file, which can be a Uint8Array, ArrayBuffer, Blob, or ReadableStream.
 * @returns A Promise that resolves to a FileCollection containing the data from the IUM file.
 */
export async function fromIum(zipContent) {
    const contentReader = getZipContentReader(zipContent);
    const zipReader = new ZipReader(contentReader);
    const zipFiles = new Map();
    for await (const entry of zipReader.getEntriesGenerator()) {
        zipFiles.set(entry.filename.replaceAll(/\/\/+/g, '/'), entry);
    }
    const indexFile = zipFiles.get('/index.json');
    if (!indexFile) {
        throw new Error('Invalid IUM file: missing index.json');
    }
    const rawData = await indexFile.getData?.(new TextWriter());
    const index = await JSON.parse(rawData ?? '');
    const fileCollection = new FileCollection(index.options);
    const promises = [];
    for (const source of index.sources) {
        const url = new URL(source.relativePath, source.baseURL);
        if (url.protocol === 'ium:') {
            const key = `/data/${url.pathname}`.replaceAll(/\/\/+/g, '/');
            const zipEntry = zipFiles.get(key);
            if (!zipEntry) {
                throw new Error(`Invalid IUM file: missing ${url.pathname}`);
            }
            promises.push(appendEntry(zipEntry, url, fileCollection));
        }
        else {
            promises.push(fileCollection.appendExtendedSource(sourceItemToExtendedSourceItem(source, undefined)));
            // should come from the web
        }
    }
    await Promise.all(promises);
    return fileCollection;
}
async function appendEntry(entry, url, fileCollection) {
    const getData = entry.getData?.bind(entry);
    if (!getData)
        return;
    const buffer = await getData(new Uint8ArrayWriter());
    await fileCollection.appendArrayBuffer(url.pathname, buffer, {
        dateModified: entry.lastModDate.getTime(),
    });
}
export const UNSUPPORTED_ZIP_CONTENT_ERROR = `Unsupported zip content type.
If you passed a Node.js Stream convert it to Web Stream.
If you passed a (binary) string, decode it to Uint8Array.`;
function getZipContentReader(zipContent) {
    if (zipContent instanceof Uint8Array) {
        return new Uint8ArrayReader(zipContent);
    }
    else if (zipContent instanceof ArrayBuffer) {
        return new Uint8ArrayReader(new Uint8Array(zipContent));
    }
    else if (zipContent instanceof Blob) {
        return new BlobReader(zipContent);
    }
    else if (zipContent instanceof ReadableStream) {
        return zipContent;
    }
    throw new Error(UNSUPPORTED_ZIP_CONTENT_ERROR);
}
//# sourceMappingURL=fromIum.js.map